import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { OrderSheetMarketCell } from "@/features/order-sheet-sets/utils/order-sheet-market-product";
import { OpenMarketExternal } from "@/features/order-sheet-sets/order-sheet-market-external-link";

// 패스픽 카드와 같은 색 — 기준가 파랑, 마진 양수 초록, 음수 빨강, 미매칭 회색.
export const MARKET_PRICE = "#1272EF";
export const MARKET_MARGIN_UP = "#16B41C";
export const MARKET_MARGIN_DOWN = "#CC1D1D";
export const MARKET_MUTED = "#8F8F8C";

interface Props {
  cell: OrderSheetMarketCell;
  fontSize?: string;
  // 넘기면 가격이 그 마켓으로 나가는 버튼이 된다(크림은 바로, 포이즌은 안내 모달).
  onOpen?: OpenMarketExternal;
}

/**
 * 마켓 한 줄의 값 — 예상 마진 금액 + 기준가(이 순서). 자세히 보기(그리드)의 시세 열이 쓴다.
 * 마진은 비율이 아니라 금액(기준가 − 예상 매입가)으로 보여준다. 가격을 누르면 그 마켓의
 * 상품 화면으로 나간다. 미매칭이면 "-" 하나만 그린다.
 */
export default function LineSheetProductMarketPriceLine({
  cell,
  fontSize = "11px",
  onOpen,
}: Props) {
  const { tCurrencyString } = useI18nHelper();

  if (cell.basisValue == null) {
    return (
      <Text fontSize={fontSize} lineHeight={"normal"} color={MARKET_MUTED}>
        -
      </Text>
    );
  }

  const priceText = tCurrencyString(cell.basisValue, {
    type: "SYMBOL",
    code: "KRW",
  });
  // 양수에는 + 를 붙여 이익/손해가 부호로도 읽히게 한다(음수는 통화 표기가 - 를 붙인다).
  const marginText =
    cell.expectedMargin != null
      ? `${cell.expectedMargin >= 0 ? "+" : ""}${tCurrencyString(
          cell.expectedMargin,
          { type: "SYMBOL", code: "KRW" }
        )}`
      : null;
  const marginColor =
    cell.expectedMargin != null && cell.expectedMargin >= 0
      ? MARKET_MARGIN_UP
      : MARKET_MARGIN_DOWN;
  const externalUrl = cell.externalUrl;

  return (
    // lineHeight 를 normal 로 되돌린다 — ag-grid 셀은 줄 높이를 행 높이에 맞춰 두어서,
    // 그대로 두면 글자 상자가 커져 가운데 정렬이 어긋난다.
    <Flex
      alignItems={"center"}
      gap={"4px"}
      fontSize={fontSize}
      lineHeight={"normal"}
    >
      {/* 마진을 앞에, 가격을 뒤에 — 가격이 열 오른쪽 끝에 맞춰져 위의 예상 매입가와 세로로 줄이 맞는다 */}
      {marginText && (
        <Text color={marginColor} fontWeight={700} whiteSpace={"nowrap"}>
          {marginText}
        </Text>
      )}
      {externalUrl && onOpen ? (
        <Text
          as={"button"}
          type={"button"}
          color={MARKET_PRICE}
          lineHeight={"normal"}
          textDecoration={"underline"}
          cursor={"pointer"}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onOpen(cell.market, externalUrl);
          }}
        >
          {priceText}
        </Text>
      ) : (
        <Text color={MARKET_PRICE}>{priceText}</Text>
      )}
    </Flex>
  );
}
